import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff, User as UserIcon, Save, X, Mail, Lock, Pencil, MailWarning, CheckCircle2, Download, Trash2 } from 'lucide-react';
import Alert from '../common/Alert';
import Button from '../common/Button';
import { authAPI } from '../../services/api';

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+|[._-]/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const Profile = () => {
  const { user, updateProfile, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent

  const handleResendVerification = async () => {
    setResendState('sending');
    const result = await authAPI.resendVerification();
    setResendState(result.success ? 'sent' : 'idle');
  };

  // Two-factor authentication
  const [twoFA, setTwoFA] = useState({ enabled: !!user?.twoFactorEnabled, setup: null, code: '', msg: '', err: '' });

  const start2FASetup = async () => {
    const r = await authAPI.setup2FA();
    if (r.success) setTwoFA((s) => ({ ...s, setup: r.data, code: '', err: '', msg: '' }));
    else setTwoFA((s) => ({ ...s, err: r.message || 'Failed to start setup' }));
  };
  const confirm2FA = async () => {
    const r = await authAPI.enable2FA(twoFA.code);
    if (r.success) setTwoFA({ enabled: true, setup: null, code: '', msg: 'Two-factor enabled.', err: '' });
    else setTwoFA((s) => ({ ...s, err: r.message || 'Invalid code' }));
  };
  const disable2FA = async () => {
    const r = await authAPI.disable2FA(twoFA.code);
    if (r.success) setTwoFA({ enabled: false, setup: null, code: '', msg: 'Two-factor disabled.', err: '' });
    else setTwoFA((s) => ({ ...s, err: r.message || 'Invalid code' }));
  };

  // Data & privacy (GDPR export + account deletion)
  const [exportState, setExportState] = useState('idle'); // idle | exporting
  const [deleteState, setDeleteState] = useState({ open: false, confirm: '', password: '', totp: '', busy: false, err: '' });

  const handleExportData = async () => {
    setExportState('exporting');
    try {
      const blob = await authAPI.exportMyData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dynalink-data-export.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to export your data. Please try again.' });
    } finally {
      setExportState('idle');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteState((s) => ({ ...s, busy: true, err: '' }));
    const r = await authAPI.deleteAccount(deleteState.password, deleteState.totp || undefined);
    if (r.success) {
      await logout();
      window.location.href = '/';
    } else {
      setDeleteState((s) => ({ ...s, busy: false, err: r.message || 'Failed to delete account' }));
    }
  };

  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (formData.password && formData.password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return false;
    }
    if (formData.password && formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const updates = {};
    if (formData.username !== user.username) updates.username = formData.username;
    if (formData.email !== user.email) updates.email = formData.email;
    if (formData.password) updates.password = formData.password;

    if (Object.keys(updates).length === 0) {
      setMessage({ type: 'info', text: 'No changes to save' });
      setLoading(false);
      return;
    }

    const result = await updateProfile(updates);

    if (result.success) {
      setMessage({ type: 'success', text: result.message });
      setIsEditing(false);
      setFormData((prev) => ({ ...prev, password: '', confirmPassword: '' }));
    } else {
      setMessage({ type: 'error', text: result.message });
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      username: user?.username || '',
      email: user?.email || '',
      password: '',
      confirmPassword: '',
    });
    setMessage({ type: '', text: '' });
  };

  if (!user) {
    return (
      <div className="text-center py-16 text-ink-500">Loading profile…</div>
    );
  }

  const fieldWrap =
    'w-full rounded-lg border bg-white text-sm text-ink-900 placeholder-ink-400 py-2.5 pl-10 pr-3.5 transition focus:outline-none focus:ring-4';
  const fieldOk = 'border-ink-200 hover:border-ink-300 focus:border-brand-500 focus:ring-brand-100';
  const fieldDisabled = 'bg-ink-50 text-ink-600 cursor-not-allowed';

  return (
    <div className="max-w-3xl mx-auto">
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">
          Profile
        </h1>
        <p className="mt-1.5 text-sm text-ink-500">
          Manage your account details and security.
        </p>
      </header>

      <div className="rounded-2xl border border-ink-200 bg-white shadow-card overflow-hidden">
        {/* Banner */}
        <div className="relative h-28 bg-gradient-to-br from-brand-500 via-violet-600 to-fuchsia-600">
          <div className="absolute inset-0 bg-grid opacity-25" />
        </div>
        <div className="px-6 sm:px-8 -mt-10 pb-6">
          <div className="flex items-end justify-between gap-4">
            <div className="flex items-end gap-4">
              <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-white text-ink-900 text-xl font-bold ring-4 ring-white shadow-card">
                <span className="inline-flex h-full w-full items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white">
                  {getInitials(user.username)}
                </span>
              </div>
              <div className="pb-1.5">
                <p className="text-lg font-semibold text-ink-900">
                  {user.username}
                </p>
                <p className="text-sm text-ink-500">{user.email}</p>
              </div>
            </div>
            {!isEditing && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsEditing(true)}
                className="mb-1.5"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </div>

        <div className="border-t border-ink-100 px-6 sm:px-8 py-6">
          {message.text && (
            <Alert
              type={message.type}
              message={message.text}
              className="mb-5"
            />
          )}

          {user.emailVerified === false && (
            <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2.5">
                <MailWarning className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Your email isn't verified</p>
                  <p className="text-xs text-amber-700">Verify your email to secure your account.</p>
                </div>
              </div>
              {resendState === 'sent' ? (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> Sent — check your inbox
                </span>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={handleResendVerification}
                  disabled={resendState === 'sending'}
                >
                  {resendState === 'sending' ? 'Sending…' : 'Resend verification'}
                </Button>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-700">
                  Username
                </label>
                <div className="relative">
                  <UserIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    disabled={!isEditing}
                    required
                    className={`${fieldWrap} ${fieldOk} ${
                      !isEditing ? fieldDisabled : ''
                    }`}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-700">
                  Email
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={!isEditing}
                    required
                    className={`${fieldWrap} ${fieldOk} ${
                      !isEditing ? fieldDisabled : ''
                    }`}
                  />
                </div>
              </div>
            </div>

            {isEditing && (
              <div className="rounded-xl border border-ink-200 bg-ink-50/40 p-5 space-y-4">
                <div>
                  <p className="text-sm font-semibold text-ink-900">
                    Change password
                  </p>
                  <p className="text-xs text-ink-500">
                    Leave blank to keep your current password.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-ink-700">
                      New password
                    </label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Min 8 characters"
                        className={`${fieldWrap} ${fieldOk} pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-ink-100"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-ink-700">
                      Confirm new password
                    </label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Repeat new password"
                        className={`${fieldWrap} ${fieldOk}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isEditing && (
              <div className="flex items-center gap-2">
                <Button type="submit" disabled={loading} variant="primary">
                  <Save className="h-4 w-4" />
                  {loading ? 'Saving…' : 'Save changes'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              </div>
            )}
          </form>

          {/* Two-factor authentication */}
          <div className="mt-8 pt-6 border-t border-ink-100">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">Two-factor authentication</h3>
                <p className="mt-0.5 text-xs text-ink-500">
                  {twoFA.enabled
                    ? 'Enabled — you’ll be asked for a code from your authenticator app at sign-in.'
                    : 'Add an extra layer of security with a TOTP authenticator app.'}
                </p>
              </div>
              {twoFA.enabled ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> Enabled
                </span>
              ) : !twoFA.setup ? (
                <Button type="button" variant="secondary" size="small" onClick={start2FASetup}>
                  Enable 2FA
                </Button>
              ) : null}
            </div>

            {twoFA.msg && <p className="mt-2 text-xs text-emerald-700">{twoFA.msg}</p>}
            {twoFA.err && <p className="mt-2 text-xs text-rose-600">{twoFA.err}</p>}

            {/* Setup flow */}
            {!twoFA.enabled && twoFA.setup && (
              <div className="mt-4 rounded-xl border border-ink-200 bg-ink-50/40 p-4 space-y-3">
                <p className="text-xs text-ink-600">
                  Add this secret to your authenticator app, then enter the 6-digit code to confirm.
                </p>
                <code className="block break-all rounded bg-white border border-ink-200 px-3 py-2 font-mono text-xs">
                  {twoFA.setup.base32}
                </code>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={twoFA.code}
                    onChange={(e) => setTwoFA((s) => ({ ...s, code: e.target.value.replace(/\s/g, '') }))}
                    placeholder="6-digit code"
                    className="flex-1 rounded-lg border border-ink-200 px-3 py-2 text-sm tracking-widest focus:outline-none focus:ring-4 focus:ring-brand-100"
                  />
                  <Button type="button" size="small" onClick={confirm2FA} disabled={!twoFA.code}>Confirm</Button>
                </div>
              </div>
            )}

            {/* Disable flow */}
            {twoFA.enabled && (
              <div className="mt-4 flex gap-2 max-w-sm">
                <input
                  type="text"
                  inputMode="numeric"
                  value={twoFA.code}
                  onChange={(e) => setTwoFA((s) => ({ ...s, code: e.target.value.replace(/\s/g, '') }))}
                  placeholder="Code to disable"
                  className="flex-1 rounded-lg border border-ink-200 px-3 py-2 text-sm tracking-widest focus:outline-none focus:ring-4 focus:ring-brand-100"
                />
                <Button type="button" variant="danger" size="small" onClick={disable2FA} disabled={!twoFA.code}>Disable</Button>
              </div>
            )}
          </div>

          {/* Data & privacy */}
          <div className="mt-8 pt-6 border-t border-ink-100 space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-ink-900">Data &amp; privacy</h3>
              <p className="mt-0.5 text-xs text-ink-500">
                Download a copy of everything we store about you, or permanently delete your account.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={handleExportData}
                disabled={exportState === 'exporting'}
              >
                <Download className="h-4 w-4" />
                {exportState === 'exporting' ? 'Preparing…' : 'Download my data'}
              </Button>
              <Button
                type="button"
                variant="danger"
                size="small"
                onClick={() => setDeleteState((s) => ({ ...s, open: !s.open }))}
              >
                <Trash2 className="h-4 w-4" />
                Delete account
              </Button>
            </div>

            {deleteState.open && (
              <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 space-y-3">
                <p className="text-sm font-medium text-rose-900">
                  This permanently deletes your account, links, profile pages, and analytics. It cannot be undone.
                </p>
                <p className="text-xs text-rose-700">
                  Type <span className="font-mono font-semibold">{user.username}</span> to confirm, then enter your password{twoFA.enabled ? ' and a 2FA code' : ''}.
                </p>
                {deleteState.err && <p className="text-xs text-rose-600 font-medium">{deleteState.err}</p>}
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={deleteState.confirm}
                    onChange={(e) => setDeleteState((s) => ({ ...s, confirm: e.target.value }))}
                    placeholder={`Type "${user.username}"`}
                    className="rounded-lg border border-rose-200 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-rose-100"
                  />
                  <input
                    type="password"
                    value={deleteState.password}
                    onChange={(e) => setDeleteState((s) => ({ ...s, password: e.target.value }))}
                    placeholder="Current password"
                    className="rounded-lg border border-rose-200 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-rose-100"
                  />
                  {twoFA.enabled && (
                    <input
                      type="text"
                      inputMode="numeric"
                      value={deleteState.totp}
                      onChange={(e) => setDeleteState((s) => ({ ...s, totp: e.target.value.replace(/\s/g, '') }))}
                      placeholder="6-digit 2FA code"
                      className="rounded-lg border border-rose-200 px-3 py-2 text-sm tracking-widest focus:outline-none focus:ring-4 focus:ring-rose-100"
                    />
                  )}
                </div>
                <Button
                  type="button"
                  variant="danger"
                  size="small"
                  onClick={handleDeleteAccount}
                  disabled={deleteState.busy || deleteState.confirm !== user.username || !deleteState.password || (twoFA.enabled && !deleteState.totp)}
                >
                  {deleteState.busy ? 'Deleting…' : 'Permanently delete my account'}
                </Button>
              </div>
            )}
          </div>

          <div className="mt-8 pt-6 border-t border-ink-100 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-ink-500">
                Member since
              </p>
              <p className="mt-1 font-medium text-ink-900">
                {user.created_at
                  ? new Date(user.created_at).toLocaleDateString()
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-ink-500">
                User ID
              </p>
              <p className="mt-1 font-mono text-xs text-ink-700">{user.id}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
